import { Button, Card, Field, Logo, Muted, Screen, Stack, color, space } from '@biopeak/ui';
import { useState, type FormEvent } from 'react';
import { supabase } from '../lib/supabase.ts';

/**
 * Pedido de redefinição de senha.
 *
 * O link volta para esta mesma origem, em `/reset-password` — funciona tanto
 * em desenvolvimento (`localhost`) quanto em produção (`serbio-health.com`)
 * porque usa `window.location.origin`, nunca uma URL fixa.
 */
export function ForgotPassword({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);

    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setBusy(false);

    // Sempre mostra sucesso, exista ou não o e-mail — a mesma razão de
    // `messageFor` no SignIn não distinguir "senha errada" de "e-mail
    // inexistente": confirmar a ausência de uma conta é informação sensível.
    // Só o rate limit é uma exceção visível, porque não vaza nada sobre a conta.
    if (resetError && /rate limit|too many/i.test(resetError.message)) {
      setError('Muitas tentativas. Aguarde um momento e tente de novo.');
      return;
    }
    setSent(true);
  }

  if (sent) {
    return (
      <Screen style={{ justifyContent: 'center', minHeight: '100vh', maxWidth: '420px' }}>
        <Stack gap={space.sm}>
          <Logo variant="lockup" size={40} />
        </Stack>
        <Card>
          <Stack gap={space.md}>
            <h1 style={{ fontSize: '18px', margin: 0 }}>Verifique seu e-mail</h1>
            <Muted>
              Se {email} tiver uma conta cadastrada, enviamos um link para
              redefinir a senha. Confira também a caixa de spam.
            </Muted>
            <Button type="button" fullWidth onClick={onBack}>
              Voltar para o login
            </Button>
          </Stack>
        </Card>
      </Screen>
    );
  }

  return (
    <Screen style={{ justifyContent: 'center', minHeight: '100vh', maxWidth: '420px' }}>
      <Stack gap={space.sm}>
        <Logo variant="lockup" size={40} />
        <Muted>Redefinir senha</Muted>
      </Stack>

      <Card>
        <form onSubmit={handleSubmit}>
          <Stack gap={space.md}>
            <Field
              label="E-mail"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            {error && (
              <p role="alert" style={{ color: '#C7332B', fontSize: '15px' }}>
                {error}
              </p>
            )}

            <Button type="submit" fullWidth disabled={busy}>
              {busy ? 'Enviando…' : 'Enviar link de redefinição'}
            </Button>
          </Stack>
        </form>
      </Card>

      <button
        type="button"
        onClick={onBack}
        style={{
          background: 'none',
          border: 'none',
          color: color.accent,
          fontSize: '15px',
          padding: space.sm,
        }}
      >
        Voltar para o login
      </button>
    </Screen>
  );
}
